import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Globe, User, LogOut, Building2, Star, Bookmark, Briefcase, Search, Heart, LayoutGrid, Video, ChevronDown, Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { useAuth as useSupabaseAuth } from '../../contexts/AuthContext';
import { useUserProfile } from '../../contexts/UserProfileContext';
import { useViewMode, ViewMode } from '../../contexts/ViewModeContext';
import { ENABLE_SAVED_PASSPORTS, isRecruiterShellEnabled } from '../../config/features';
import InboxWidget from '../Inbox/InboxWidget';
import { useLanguage } from '../../contexts/LanguageContext';
import { rememberPostAuthRedirect } from '../../lib/postAuthRedirect';

const Header: React.FC = () => {
  const { t } = useTranslation('common');
  const { isSignedIn, user } = useAuth();
  const { signOut } = useSupabaseAuth();
  const { userProfile } = useUserProfile();
  const { viewMode, setViewMode, isViewingAsRecruiter } = useViewMode();
  const location = useLocation();
  const navigate = useNavigate();

  // Passport share deep-links: Header Anmelden/Registrieren must bring the user
  // back to /p/:token (same as the page's own gate CTAs). Without this, login
  // lands on the default home and the share URL is lost.
  const isPassportShare = location.pathname.startsWith('/p/');
  const shareReturnTo = location.pathname + location.search;
  const headerLoginTo = isPassportShare
    ? `/login?mode=recruiter&return=${encodeURIComponent(shareReturnTo)}`
    : '/login';
  const headerSignupTo = isPassportShare
    ? `/recruiter-signup?return=${encodeURIComponent(shareReturnTo)}`
    : '/signup';
  const rememberShareReturn = () => {
    if (isPassportShare) rememberPostAuthRedirect(shareReturnTo);
  };

  const handleSwitchMode = (mode: ViewMode) => {
    const targetTab = mode === 'recruiter' ? 'hire' : 'explore';
    const currentTab = new URLSearchParams(location.search).get('tab');
    const modeChanged = mode !== viewMode;
    const tabMismatch = currentTab !== targetTab;

    // Always realign when URL tab and target diverge — signed-out users can end
    // up with viewMode=explorer while ?tab=hire is still active (early return
    // used to skip navigate in that case).
    if (!modeChanged && !tabMismatch) return;

    if (modeChanged) setViewMode(mode);
    if (tabMismatch) {
      navigate(mode === 'recruiter' ? '/?tab=hire' : '/?tab=explore');
    }
  };
  const userMenuRef = useRef<HTMLDivElement>(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const viewModeRef = useRef<HTMLDivElement>(null);
  const [viewModeOpen, setViewModeOpen] = useState(false);
  const { language, toggleLanguage } = useLanguage();

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
      if (viewModeRef.current && !viewModeRef.current.contains(event.target as Node)) {
        setViewModeOpen(false);
      }
    };

    if (userMenuOpen || viewModeOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuOpen, viewModeOpen]);
  
  // Handle sign out
  const handleSignOut = async () => {
    try {
      await signOut();
      setUserMenuOpen(false);
      
      // Smart redirect after signout based on current page
      if (location.pathname === '/profile' || location.pathname === '/recruiter-profile') {
        navigate('/');
      }
      // For upload page, stay on upload (will show auth prompt)
      // For other pages, stay where they are
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  return (
    <header className="sticky top-0 z-[10000] bg-white/10 backdrop-blur-2xl backdrop-saturate-150 border-b border-slate-200/80 shadow-[0_1px_3px_rgba(0,0,0,0.03)]">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="relative flex justify-between items-stretch h-11 sm:h-[60px] pl-0">
          {/* Logo + View-Mode Switch */}
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <button 
              onClick={() => {
                const homeTab = isViewingAsRecruiter ? '/?tab=hire' : '/?tab=explore';
                navigate(homeTab);
              }}
              className="flex items-center cursor-pointer flex-shrink-0"
            >
              <span 
                className="text-[1.25rem] sm:text-[1.7rem] leading-none bg-clip-text text-transparent block"
                style={{
                  backgroundImage: 'radial-gradient(circle at 52% center, #fde047 0%, #f9a8d4 25%, #db2777 55%, #881337 100%)',
                  fontFamily: 'Archivo Black, sans-serif'
                }}
              >
                Cevee
              </span>
            </button>

            {/* For explorers / For recruiters switch — pill on desktop */}
            <div className="hidden sm:flex items-center flex-shrink-0">
              <div className="flex items-center rounded-full bg-slate-100 p-0.5 text-[10px] sm:text-xs font-semibold">
                <button
                  onClick={() => handleSwitchMode('explorer')}
                  aria-pressed={!isViewingAsRecruiter}
                  className={`rounded-full px-2 sm:px-3 py-0.5 sm:py-1 transition-colors whitespace-nowrap ${
                    !isViewingAsRecruiter
                      ? 'bg-white text-pink-600 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {t('header.viewExplorers', 'Explorers')}
                </button>
                <button
                  onClick={() => handleSwitchMode('recruiter')}
                  aria-pressed={isViewingAsRecruiter}
                  className={`rounded-full px-2 sm:px-3 py-0.5 sm:py-1 transition-colors whitespace-nowrap ${
                    isViewingAsRecruiter
                      ? 'bg-white text-pink-600 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {t('header.viewRecruiters', 'Recruiters')}
                </button>
              </div>
            </div>

            {/* Same switch as a compact dropdown on mobile (saves space) */}
            <div className="relative sm:hidden flex items-center flex-shrink-0" ref={viewModeRef}>
              <button
                onClick={() => setViewModeOpen((o) => !o)}
                className="flex items-center gap-0.5 rounded-full bg-slate-100 pl-2.5 pr-1.5 py-1 text-[10px] font-semibold text-pink-600 whitespace-nowrap"
              >
                {isViewingAsRecruiter ? t('header.viewRecruiters', 'Recruiters') : t('header.viewExplorers', 'Explorers')}
                <ChevronDown className={`w-3 h-3 text-slate-400 transition-transform ${viewModeOpen ? 'rotate-180' : ''}`} />
              </button>
              {viewModeOpen && (
                <div className="absolute left-0 top-full mt-1 w-32 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-[10000]">
                  <button
                    onClick={() => { handleSwitchMode('explorer'); setViewModeOpen(false); }}
                    className={`block w-full text-left px-3 py-1.5 text-[11px] font-semibold transition-colors ${
                      !isViewingAsRecruiter ? 'text-pink-600 bg-pink-50' : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {t('header.viewExplorers', 'Explorers')}
                  </button>
                  <button
                    onClick={() => { handleSwitchMode('recruiter'); setViewModeOpen(false); }}
                    className={`block w-full text-left px-3 py-1.5 text-[11px] font-semibold transition-colors ${
                      isViewingAsRecruiter ? 'text-pink-600 bg-pink-50' : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {t('header.viewRecruiters', 'Recruiters')}
                  </button>
                </div>
              )}
            </div>
          </div>
          
          {/* Hidden Desktop Navigation - App Style */}

          {/* App-Style Menu Button (Always Visible) */}
          <div className="flex items-center space-x-1 sm:space-x-2.5 self-center">
            {/* Language Switcher */}
            <button
              onClick={toggleLanguage}
              className="text-slate-500 hover:text-slate-700 transition-colors p-1 sm:p-1.5 rounded-lg hover:bg-slate-100 flex items-center gap-0.5"
              title={`Switch to ${language === 'en' ? 'Deutsch' : 'English'}`}
            >
              <Globe className="w-3.5 h-3.5 sm:w-[18px] sm:h-[18px]" />
              <span className="text-[11px] sm:text-[13px] font-semibold">{language.toUpperCase()}</span>
            </button>

            {/* Auth Buttons */}
            {!isSignedIn && (
              <div className="flex items-center space-x-1 sm:space-x-1.5">
                <Link
                  to={headerLoginTo}
                  onClick={rememberShareReturn}
                  className="px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-slate-600 hover:text-slate-900 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  {t('header.signIn')}
                </Link>
                <Link
                  to={headerSignupTo}
                  onClick={rememberShareReturn}
                  className="px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm font-semibold text-white bg-gradient-to-r from-pink-500 to-rose-500 rounded-lg hover:from-pink-600 hover:to-rose-600 transition-all shadow-sm whitespace-nowrap"
                >
                  {t('header.getStarted')}
                </Link>
              </div>
            )}

            {/* Inbox + User for signed in */}
            {isSignedIn && (
              <div className="flex items-center space-x-1.5">
                <InboxWidget />
                
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    onMouseEnter={() => { if (window.matchMedia('(hover: hover)').matches) setUserMenuOpen(true); }}
                    className="flex items-center justify-center h-6 w-6 sm:h-8 sm:w-8 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 text-white text-[11px] sm:text-sm font-semibold hover:opacity-90 transition-opacity"
                    title={user?.email || 'User menu'}
                  >
                    {user?.firstName ? user.firstName[0].toUpperCase() : <User className="w-3 h-3 sm:w-3.5 sm:h-3.5" />}
                  </button>
                  
                  {userMenuOpen && (
                    <div 
                      className="absolute right-0 top-full mt-1 w-52 sm:w-64 bg-white rounded-xl shadow-lg shadow-slate-200/50 border border-slate-200 py-1.5 z-[10000]"
                      onMouseLeave={() => setUserMenuOpen(false)}
                    >
                      <Link
                        to={userProfile?.is_recruiter ? "/recruiter-profile" : "/profile"}
                        onClick={() => setUserMenuOpen(false)}
                        className="px-3 py-2 sm:py-2.5 text-[13px] sm:text-base text-slate-700 hover:bg-slate-50 hover:text-pink-600 transition-colors rounded-lg mx-1.5 flex items-center gap-2.5"
                      >
                        <User className="w-3.5 h-3.5 sm:w-[18px] sm:h-[18px] text-slate-400" /> {t('header.profile')}
                      </Link>

                      {!userProfile?.is_recruiter && (
                        <Link
                          to="/?tab=inspire"
                          onClick={() => setUserMenuOpen(false)}
                          className="px-3 py-2 sm:py-2.5 text-[13px] sm:text-base text-slate-700 hover:bg-slate-50 hover:text-pink-600 transition-colors rounded-lg mx-1.5 flex items-center gap-2.5"
                        >
                          <Upload className="w-3.5 h-3.5 sm:w-[18px] sm:h-[18px] text-pink-400" /> {t('header.cvUpload', 'CV Upload')}
                        </Link>
                      )}
                      
                      {isViewingAsRecruiter && (
                        <Link to="/company/manage" onClick={() => setUserMenuOpen(false)}
                          className="px-3 py-2 sm:py-2.5 text-[13px] sm:text-base text-slate-700 hover:bg-slate-50 hover:text-pink-600 transition-colors rounded-lg mx-1.5 flex items-center gap-2.5">
                          <Building2 className="w-3.5 h-3.5 sm:w-[18px] sm:h-[18px] text-slate-400" /> {t('header.companyManage', 'My Company')}
                        </Link>
                      )}

                      {isViewingAsRecruiter && !isRecruiterShellEnabled() && (
                        <>
                          <div className="border-t border-slate-100 my-1 mx-2" />
                          <Link to="/?tab=hire" onClick={() => setUserMenuOpen(false)}
                            className="px-3 py-2 sm:py-2.5 text-[13px] sm:text-base text-slate-700 hover:bg-slate-50 hover:text-pink-600 transition-colors rounded-lg mx-1.5 flex items-center gap-2.5">
                            <Briefcase className="w-3.5 h-3.5 sm:w-[18px] sm:h-[18px] text-pink-400" /> Hiring Board
                          </Link>
                          <Link to="/pipeline" onClick={() => setUserMenuOpen(false)}
                            className="px-3 py-2 sm:py-2.5 text-[13px] sm:text-base text-slate-700 hover:bg-slate-50 hover:text-pink-600 transition-colors rounded-lg mx-1.5 flex items-center gap-2.5">
                            <LayoutGrid className="w-3.5 h-3.5 sm:w-[18px] sm:h-[18px] text-pink-400" /> Pipeline
                          </Link>
                          <Link to="/candidate-search" onClick={() => setUserMenuOpen(false)}
                            className="px-3 py-2 sm:py-2.5 text-[13px] sm:text-base text-slate-700 hover:bg-slate-50 hover:text-pink-600 transition-colors rounded-lg mx-1.5 flex items-center gap-2.5">
                            <Search className="w-3.5 h-3.5 sm:w-[18px] sm:h-[18px] text-slate-400" /> Candidate Search
                          </Link>
                          <Link to="/interested" onClick={() => setUserMenuOpen(false)}
                            className="px-3 py-2 sm:py-2.5 text-[13px] sm:text-base text-slate-700 hover:bg-slate-50 hover:text-pink-600 transition-colors rounded-lg mx-1.5 flex items-center gap-2.5">
                            <Heart className="w-3.5 h-3.5 sm:w-[18px] sm:h-[18px] text-pink-400" /> Interested
                          </Link>
                          <Link to="/spotlights" onClick={() => setUserMenuOpen(false)}
                            className="px-3 py-2 sm:py-2.5 text-[13px] sm:text-base text-slate-700 hover:bg-slate-50 hover:text-pink-600 transition-colors rounded-lg mx-1.5 flex items-center gap-2.5">
                            <Star className="w-3.5 h-3.5 sm:w-[18px] sm:h-[18px] text-amber-400" /> Spotlight
                          </Link>
                          <Link to="/interviews" onClick={() => setUserMenuOpen(false)}
                            className="px-3 py-2 sm:py-2.5 text-[13px] sm:text-base text-slate-700 hover:bg-slate-50 hover:text-pink-600 transition-colors rounded-lg mx-1.5 flex items-center gap-2.5">
                            <Video className="w-3.5 h-3.5 sm:w-[18px] sm:h-[18px] text-pink-400" /> AI Interviews
                            <span className="ml-auto text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-pink-50 text-pink-500 border border-pink-100 uppercase tracking-wider">Pilot</span>
                          </Link>
                        </>
                      )}

                      {!isViewingAsRecruiter && ENABLE_SAVED_PASSPORTS && (
                        <Link to="/saved" onClick={() => setUserMenuOpen(false)}
                          className="px-3 py-2 sm:py-2.5 text-[13px] sm:text-base text-slate-700 hover:bg-slate-50 hover:text-pink-600 transition-colors rounded-lg mx-1.5 flex items-center gap-2.5">
                          <Bookmark className="w-3.5 h-3.5 sm:w-[18px] sm:h-[18px] text-slate-400" /> {t('header.saved')}
                        </Link>
                      )}

                      <div className="border-t border-slate-100 my-1 mx-2" />
                      
                      <button
                        onClick={handleSignOut}
                        className="flex items-center gap-2.5 w-full px-3 py-2 sm:py-2.5 text-[13px] sm:text-base text-slate-700 hover:bg-slate-50 hover:text-red-500 transition-colors rounded-lg mx-1.5"
                      >
                        <LogOut className="w-3.5 h-3.5 sm:w-[18px] sm:h-[18px] flex-shrink-0" />
                        {t('header.signOut')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
